import * as React from "react";
import {
  AreaChart as RechartsAreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "./chart";
import { cn } from "@/lib/utils";

export interface AreaChartProps {
  data: any[];
  className?: string;
  config?: any;
  variant?: "default" | "animated" | "interactive" | "minimal";
  theme?: "light" | "dark" | "minimal";
  responsive?: boolean;
  accessible?: boolean;
  gradient?: boolean;
  dataKey?: string;
  strokeColor?: string;
  fillColor?: string;
  strokeWidth?: number;
  fillOpacity?: number;
  type?:
    | "monotone"
    | "linear"
    | "step"
    | "stepBefore"
    | "stepAfter"
    | "basis"
    | "basisOpen"
    | "basisClosed"
    | "natural"
    | "monotoneX"
    | "monotoneY";
}

export function AreaChart({
  data,
  className,
  config = {},
  variant = "default",
  theme = "light",
  responsive = true,
  accessible = true,
  gradient = true,
  dataKey = "value",
  strokeColor,
  fillColor,
  strokeWidth = 2,
  fillOpacity = 0.3,
  type = "monotone",
  ...props
}: AreaChartProps) {
  const chartId = React.useId();

  // Generate gradient ID
  const gradientId = `area-gradient-${chartId}`;

  // Determine colors based on theme
  const getThemeColors = () => {
    switch (theme) {
      case "dark":
        return {
          stroke: strokeColor || "#60a5fa",
          fill: fillColor || "url(#" + gradientId + ")",
          opacity: fillOpacity || 0.2,
        };
      case "minimal":
        return {
          stroke: strokeColor || "#6b7280",
          fill: fillColor || "url(#" + gradientId + ")",
          opacity: fillOpacity || 0.1,
        };
      default:
        return {
          stroke: strokeColor || "#3b82f6",
          fill: fillColor || "url(#" + gradientId + ")",
          opacity: fillOpacity || 0.3,
        };
    }
  };

  const colors = getThemeColors();

  return (
    <div
      className={cn(
        "wl-area-chart",
        "wl-area-chart-theme",
        `wl-area-chart-theme--${theme}`,
        variant !== "default" && `wl-area-chart-${variant}`,
        responsive && "wl-area-chart-responsive",
        accessible && "wl-area-chart-accessible",
        gradient && "wl-area-chart-gradient",
        className
      )}
      data-chart-type="area"
      {...props}
    >
      <ChartContainer config={config}>
        <RechartsAreaChart data={data}>
          {gradient && (
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={colors.stroke} stopOpacity={0.8} />
                <stop offset="95%" stopColor={colors.stroke} stopOpacity={0} />
              </linearGradient>
            </defs>
          )}
          <XAxis
            dataKey="name"
            stroke="#888888"
            fontSize={12}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            stroke="#888888"
            fontSize={12}
            tickLine={false}
            axisLine={false}
          />
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
          <ChartTooltip content={<ChartTooltipContent />} />
          <Area
            type={type}
            dataKey={dataKey}
            stroke={colors.stroke}
            strokeWidth={strokeWidth}
            fillOpacity={colors.opacity}
            fill={colors.fill}
          />
        </RechartsAreaChart>
      </ChartContainer>
    </div>
  );
}

// Export for convenience
export { AreaChart as default };
